/**
 * @fileoverview Top-level (lv1) curriculum card. Shows the framework color dot,
 * name, an "N assessed" badge and a chevron. Collapsed by default; disabled and
 * greyed out when the framework has no assessed items.
 */

import { useMemo, useState } from 'react'
import { Badge, Box, Collapse, Group, Paper, Text, UnstyledButton } from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { CoverageRow } from './CoverageRow'
import { countAssessedLeaves, pruneToAssessed } from '../utils/tree'
import type { ReportLv1 } from '../types'

interface Level1CardProps {
  readonly item: ReportLv1
  readonly scoreTooltip: string
}

const COLOR_DOT_SIZE = 10

export function Level1Card({ item, scoreTooltip }: Level1CardProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const assessedCount = useMemo(() => countAssessedLeaves(item.children), [item.children])
  const assessedChildren = useMemo(() => pruneToAssessed(item.children), [item.children])
  const disabled = !item.hasAssessment || assessedCount === 0

  const dot = (
    <Box
      style={{
        width: COLOR_DOT_SIZE,
        height: COLOR_DOT_SIZE,
        borderRadius: '50%',
        backgroundColor: item.color,
        flexShrink: 0,
      }}
    />
  )

  if (disabled) {
    return (
      <Paper withBorder radius='md' p='md' style={{ opacity: 0.5 }}>
        <Group spacing='sm' noWrap>
          {dot}
          <Text size='sm' weight={500} color='dimmed'>
            {item.name}
          </Text>
        </Group>
      </Paper>
    )
  }

  return (
    <Paper withBorder radius='md' p={0}>
      <UnstyledButton onClick={() => setOpen(value => !value)} p='md' style={{ width: '100%' }}>
        <Group position='apart' align='center' noWrap>
          <Group spacing='sm' noWrap style={{ minWidth: 0 }}>
            {dot}
            <Text size='sm' weight={600} lineClamp={2}>
              {item.name}
            </Text>
          </Group>
          <Group spacing='sm' noWrap style={{ flexShrink: 0 }}>
            <Badge color='green' variant='light' radius='sm'>
              {t('development_report.assessed_badge', { count: assessedCount })}
            </Badge>
            {open ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
          </Group>
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Box pb='sm'>
          {assessedChildren.map(child => (
            <CoverageRow key={child.id} node={child} depth={1} scoreTooltip={scoreTooltip} />
          ))}
        </Box>
      </Collapse>
    </Paper>
  )
}
